import './App.css';
import React, { createContext, useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route
} from "react-router-dom";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import Home from "./components/Home/Home";
import Photos from "./components/Photos/Photos";
import Illustrations from "./components/Illustrations/Illustrations";
import Videos from "./components/Videos/Videos";
import Login from "./components/Login/Login";
import Favorites from "./components/Favorites/Favorites";
import Collections from "./components/Collections/Collections";
import Blog from "./components/Blog/Blog";
import BlogPost from "./components/Blog/BlogPost";
import FAQ from "./components/FAQ/FAQ";
import CountDown from './components/countDown/CountDown';
import NotFound from "./components/NotFound/NotFound";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import RequireAuth from "./components/RequireAuth/RequireAuth";
import { DownloadProvider } from "./context/DownloadContext";
import { AuthProvider } from "./context/AuthContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { CollectionsProvider } from "./context/CollectionsContext";
import BuyCoffeeModal from "./components/BuyCoffeeModal/BuyCoffeeModal";

// Heavy (konva + filerobot-image-editor) — only loaded when the /edit route is opened.
const ImageEditor = lazy(() => import("./components/ImageEditor/ImageEditor"));

export const context = createContext();


function App() {

  // Fall back to the OS preference until the user picks a theme themselves.
  const preStore = localStorage.getItem('dark-mode');
  const [isDarkMode, setIsDarkMode] = useState(
    preStore !== null
      ? JSON.parse(preStore)
      : window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  );

  useEffect(()=>{
    window.document.body.className = (isDarkMode?"dark-mode":"light-mode");
    window.document.documentElement.classList.toggle("dark", isDarkMode);
    // Lets the `prefers-color-scheme` rule in index.css know the choice is
    // explicit, so native chrome (scrollbars, form controls) matches the page.
    window.document.documentElement.dataset.theme = isDarkMode ? "dark" : "light";
  }, [isDarkMode])

  // Keep following the OS while the user hasn't chosen a theme.
  useEffect(() => {
    if (localStorage.getItem('dark-mode') !== null) return undefined;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return undefined;
    const onChange = (e) => setIsDarkMode(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <context.Provider value={{isDarkMode, setIsDarkMode}}>
      <AuthProvider>
        <FavoritesProvider>
          <CollectionsProvider>
          <DownloadProvider>
            <Router>
              <ScrollToTop />
              <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900 transition-colors duration-300">
                <Navbar />
                <main className="flex-1">
                  <Suspense fallback={<div className="py-24 text-center text-gray-400">Loading…</div>}>
                  <Routes>
                    <Route path='/' element={<Home />} />
                    <Route path='/photos' element={<Photos />} />
                    <Route path='/illustrations' element={<Illustrations />} />
                    <Route path='/videos' element={<Videos />} />
                    <Route path='/favorites' element={<Favorites />} />
                    <Route path='/collections' element={<Collections />} />
                    <Route path='/edit' element={<RequireAuth><ImageEditor /></RequireAuth>} />
                    <Route path='/blog' element={<Blog />} />
                    <Route path='/blog/:slug' element={<BlogPost />} />
                    <Route path='/faq' element={<FAQ />} />
                    <Route path='/login' element={<Login />} />
                    <Route path='/count' element={<CountDown />} />
                    <Route path='*' element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </main>
                <Footer />
              </div>
              <BuyCoffeeModal />
            </Router>
          </DownloadProvider>
          </CollectionsProvider>
        </FavoritesProvider>
      </AuthProvider>
    </context.Provider>
  );
}

export default App;
